import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Wallet, ArrowDownToLine, ArrowUpFromLine, Receipt, Lock, Unlock, Trash2, TrendingUp, AlertTriangle, Paperclip, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const onErr = (e) => toast.error(e?.response?.data?.detail || "Request failed");
const ENTRY_META = {
  allocation: { label: "Extra allocation", icon: ArrowUpFromLine, tone: "text-slate-700" },
  cost: { label: "Project cost", icon: Receipt, tone: "text-red-700" },
  return: { label: "Return to cash", icon: ArrowDownToLine, tone: "text-emerald-700" },
};
const today = () => new Date().toISOString().slice(0, 10);
const signed = (v) => `${v < 0 ? "−" : v > 0 ? "+" : ""}${fmtMoney(Math.abs(v))}`;

/** Financing → Project Funds: KINOS money allocated to projects, returns, open balance, profit. */
export default function ProjectFunds() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const fromInvoice = searchParams.get("from_invoice");
  const { data: srcInvoice } = useQuery({ queryKey: ["invoice", fromInvoice], queryFn: () => api.getInvoice(fromInvoice), enabled: !!fromInvoice });
  const prefill = srcInvoice ? {
    name: `${srcInvoice.client_snapshot?.name || "Project"} — ${srcInvoice.invoice_number}`,
    client_id: srcInvoice.client_id || "", reference: srcInvoice.invoice_number,
    allocated: "", notes: srcInvoice.project_details?.project_name || "",
  } : null;
  useEffect(() => { if (prefill) setCreateOpen(true); }, [!!prefill]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: summary } = useQuery({ queryKey: ["fund-cash"], queryFn: api.projectFundsCashSummary });
  const { data: funds = [], isLoading } = useQuery({
    queryKey: ["project-funds", status],
    queryFn: () => api.listProjectFunds(status === "all" ? {} : { status }),
  });
  const active = funds.find((f) => f.id === activeId) || null;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["project-funds"] }); qc.invalidateQueries({ queryKey: ["fund-cash"] }); };

  return (
    <div data-testid="project-funds-page">
      <PageHeader overline="Financing" title="Project Funds"
        subtitle="Money KINOS puts into projects, what comes back to cash on hand, and the profit on each."
        actions={<Button data-testid="fund-new-btn" onClick={() => setCreateOpen(true)}><Plus className="size-4 mr-1.5" /> Allocate to project</Button>} />

      {summary && <CashSummary s={summary} />}
      <DigestBar />

      <div className="flex items-center gap-2 mb-3">
        {[["all", "All"], ["open", "Open"], ["closed", "Closed"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`fund-filter-${v}`} onClick={() => setStatus(v)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${status === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-border"}`}>{l}</button>
        ))}
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : funds.length === 0 ? (
        <div data-testid="funds-empty"><EmptyState title="No project funds yet" hint="Allocate KINOS money to a project to start tracking it." /></div>
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              {["Project", "Date", "Allocated", "Spent", "Returned", "Open balance", "Profit / loss", "Status"].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {funds.map((f) => (
                <TableRow key={f.id} data-testid={`fund-row-${f.id}`} className="cursor-pointer" onClick={() => setActiveId(f.id)}>
                  <TableCell className="font-medium">{f.name}
                    <div className="text-[10px] text-muted-foreground">{[f.client_name, f.reference].filter(Boolean).join(" · ")}</div>
                    {f.totals.spend_alert && <SpendAlertPill fund={f} />}</TableCell>
                  <TableCell className="mono text-xs">{fmtDate(f.date)}</TableCell>
                  <TableCell className="mono text-sm">{fmtMoney(f.totals.allocated)}</TableCell>
                  <TableCell className="mono text-sm text-red-700">{fmtMoney(f.totals.spent)}</TableCell>
                  <TableCell className="mono text-sm text-emerald-700">{fmtMoney(f.totals.returned)}</TableCell>
                  <TableCell className={`mono text-sm font-semibold ${f.totals.open_balance < 0 ? "text-red-700" : ""}`} data-testid={`fund-open-${f.id}`}>{fmtMoney(f.totals.open_balance)}</TableCell>
                  <TableCell className={`mono text-sm font-semibold ${f.totals.profit >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={`fund-profit-${f.id}`}>
                    {f.status === "closed" ? signed(f.totals.profit) : <span className="text-muted-foreground font-normal">{signed(f.totals.profit)} <span className="text-[10px]">(running)</span></span>}
                  </TableCell>
                  <TableCell><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset ${f.status === "open" ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-slate-100 text-slate-600 ring-slate-200"}`}>{f.status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateFundDialog key={prefill ? prefill.reference : "blank"} open={createOpen} prefill={prefill}
                        onClose={() => { setCreateOpen(false); if (fromInvoice) setSearchParams({}); }} onDone={refresh} />
      <FundDetailDialog fund={active} onClose={() => setActiveId(null)} onDone={refresh} />
    </div>
  );
}

function DigestBar() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["reminder-settings"], queryFn: api.getReminderSettings });
  const enabled = settings?.fund_digest_enabled ?? true;
  const toggle = useMutation({
    mutationFn: (v) => api.updateReminderSettings({ fund_digest_enabled: v }),
    onSuccess: (_, v) => { toast.success(v ? "Monthly fund digest on — sent on the 1st" : "Monthly fund digest off"); qc.invalidateQueries({ queryKey: ["reminder-settings"] }); },
    onError: onErr,
  });
  const send = useMutation({
    mutationFn: api.sendFundDigestNow,
    onSuccess: (r) => toast.success(`Digest for ${r.period} ${r.detail} (${r.funds} open fund${r.funds === 1 ? "" : "s"})`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Digest could not be sent"),
  });
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 rounded-md border border-border bg-slate-50 px-4 py-2.5 text-sm" data-testid="fund-digest-bar">
      <Mail className="size-4 text-primary" />
      <div className="flex-1 min-w-[220px]">
        <p className="font-medium">Monthly digest to admin &amp; finance</p>
        <p className="text-xs text-muted-foreground">On the 1st of each month: every open fund's allocation, costs, returns, open balance and profit — table in the email, branded PDF attached.</p>
      </div>
      <Switch data-testid="fund-digest-toggle" checked={enabled} disabled={!settings || toggle.isPending} onCheckedChange={(v) => toggle.mutate(v)} />
      <Button size="sm" variant="outline" data-testid="fund-digest-pdf-btn" onClick={() => api.openFundDigestPdf().catch(onErr)}><FileText className="size-3.5 mr-1" /> Preview PDF</Button>
      <Button size="sm" variant="outline" data-testid="fund-digest-send-btn" disabled={!enabled || send.isPending} onClick={() => send.mutate()}>{send.isPending ? "Sending…" : "Send now"}</Button>
    </div>
  );
}

function SpendAlertPill({ fund }) {
  const over = fund.totals.spend_alert === "over";
  return (
    <span data-testid={`fund-alert-${fund.id}`} data-level={fund.totals.spend_alert}
          className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset ${over ? "bg-red-50 text-red-700 ring-red-200" : "bg-amber-50 text-amber-800 ring-amber-200"}`}>
      <AlertTriangle className="size-3" /> {over ? `Over budget by ${fmtMoney(fund.totals.spent - fund.totals.allocated)}` : `${fund.totals.spend_pct}% of allocation spent`}
    </span>
  );
}

function SpendAlertBanner({ fund }) {
  const t = fund.totals;
  if (!t.spend_alert) return null;
  const over = t.spend_alert === "over";
  return (
    <div data-testid="fund-alert-banner" data-level={t.spend_alert}
         className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${over ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
      <AlertTriangle className="size-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold">{over ? "Project costs exceed the allocated money" : "Project costs are close to the allocated money"}</p>
        <p className="text-xs">{fmtMoney(t.spent)} spent of {fmtMoney(t.allocated)} allocated ({t.spend_pct}%).{over ? ` Overspend ${fmtMoney(t.spent - t.allocated)} — top up with an extra allocation or review the tagged expenses.` : " Consider an extra allocation before more costs are booked."}</p>
      </div>
    </div>
  );
}

function CashSummary({ s }) {
  const cards = [
    ["Cash on hand", s.cash_on_hand, "bg-slate-900 text-white", "fund-cash-on-hand"],
    ["Allocated to projects", s.allocated, "bg-white", "fund-sum-allocated"],
    ["Returned to cash", s.returned, "bg-white text-emerald-700", "fund-sum-returned"],
    ["Open balance", s.open_balance, "bg-white", "fund-sum-open"],
    ["Profit (closed projects)", s.closed_profit, s.closed_profit >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700", "fund-sum-profit"],
  ];
  return (
    <div className="mb-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(([l, v, tone, tid]) => (
          <div key={l} data-testid={tid} className={`rounded-md border border-border p-4 ${tone}`}>
            <p className="overline opacity-80 flex items-center gap-1">{l === "Cash on hand" && <Wallet className="size-3" />}{l}</p>
            <p className="mt-1 text-xl font-bold mono">{l.startsWith("Profit") ? signed(v) : fmtMoney(v)} <span className="text-xs font-normal">{s.currency}</span></p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1" data-testid="fund-cash-formula">
        <TrendingUp className="size-3" /> Cash on hand = opening {fmtMoney(s.opening_cash)} (Settings) + payments received {fmtMoney(s.payments_received)} + returns {fmtMoney(s.returned)} − expenses {fmtMoney(s.expenses)} − allocations {fmtMoney(s.allocated)} · USD only
      </p>
    </div>
  );
}

function CreateFundDialog({ open, onClose, onDone, prefill = null }) {
  const empty = { name: "", client_id: "", reference: "", allocated: "", date: today(), notes: "", ...(prefill || {}) };
  const [f, setF] = useState(empty);
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.listClients(), enabled: open });
  const mut = useMutation({
    mutationFn: () => api.createProjectFund({ ...f, allocated: Number(f.allocated), client_id: f.client_id || null }),
    onSuccess: () => { toast.success("Project fund created"); setF(empty); onClose(); onDone(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create fund"),
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="fund-create-dialog">
        <DialogHeader><DialogTitle>Allocate KINOS money to a project</DialogTitle>
          <DialogDescription className="text-xs">The allocation leaves cash on hand until it is returned.</DialogDescription></DialogHeader>
        <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
          <div className="sm:col-span-2"><Label className="overline">Project name *</Label><Input data-testid="fund-name" value={f.name} onChange={set("name")} required /></div>
          <div><Label className="overline">Allocated amount (USD) *</Label><Input data-testid="fund-allocated" type="number" min="0.01" step="0.01" value={f.allocated} onChange={set("allocated")} required /></div>
          <div><Label className="overline">Date</Label><DatePicker testId="fund-date" value={f.date} onChange={set("date")} /></div>
          <div><Label className="overline">Client (optional)</Label>
            <Select value={f.client_id || "none"} onValueChange={(v) => setF((s) => ({ ...s, client_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="fund-client"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— none —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="overline">Reference (invoice / quote #)</Label><Input data-testid="fund-reference" value={f.reference} onChange={set("reference")} /></div>
          <div className="sm:col-span-2"><Label className="overline">Notes</Label><Textarea data-testid="fund-notes" rows={2} value={f.notes} onChange={set("notes")} /></div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="fund-create-save" disabled={mut.isPending || !f.name || !f.allocated}>Allocate</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FundDetailDialog({ fund, onClose, onDone }) {
  const [entry, setEntry] = useState({ type: "return", amount: "", date: today(), note: "" });

  const addMut = useMutation({
    mutationFn: () => api.addProjectFundEntry(fund.id, { ...entry, amount: Number(entry.amount) }),
    onSuccess: (d) => {
      toast.success("Entry recorded");
      if (d?.alert_emailed) toast.warning(d.alert_emailed === "over" ? "Fund is over budget — finance has been emailed" : "Fund reached 90% of allocation — finance has been emailed", { duration: 6000 });
      setEntry((s) => ({ ...s, amount: "", note: "" })); onDone();
    },
    onError: onErr,
  });
  const delEntry = useMutation({ mutationFn: (eid) => api.deleteProjectFundEntry(fund.id, eid), onSuccess: onDone, onError: onErr });
  const statusMut = useMutation({
    mutationFn: (status) => api.updateProjectFund(fund.id, { status }),
    onSuccess: (d) => { toast.success(d.status === "closed" ? "Project closed — profit locked in" : "Project reopened"); onDone(); },
    onError: onErr,
  });
  const delFund = useMutation({
    mutationFn: () => api.deleteProjectFund(fund.id),
    onSuccess: () => { toast.success("Project fund deleted"); onClose(); onDone(); }, onError: onErr,
  });
  if (!fund) return null;
  const t = fund.totals;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="fund-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{fund.name}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset ${fund.status === "open" ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-slate-100 text-slate-600 ring-slate-200"}`}>{fund.status}</span></DialogTitle>
          <DialogDescription className="text-xs">{[fund.client_name, fund.reference, fmtDate(fund.date)].filter(Boolean).join(" · ")}{fund.notes ? ` — ${fund.notes}` : ""}</DialogDescription>
        </DialogHeader>
        <SpendAlertBanner fund={fund} />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          {[["Allocated", t.allocated, ""], ["Spent", t.spent, "text-red-700"], ["Returned", t.returned, "text-emerald-700"],
            ["Open balance", t.open_balance, t.open_balance < 0 ? "text-red-700" : ""], ["Profit / loss", t.profit, t.profit >= 0 ? "text-emerald-700" : "text-red-700"]].map(([l, v, c]) => (
            <div key={l} className="rounded-md border border-border bg-slate-50 p-2" data-testid={`fund-detail-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="overline">{l}</p><p className={`mono font-semibold mt-0.5 ${c}`}>{l.startsWith("Profit") ? signed(v) : fmtMoney(v)}</p>
            </div>
          ))}
        </div>

        {fund.status === "open" && (
          <form className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end border border-border rounded-md p-3 bg-white" data-testid="fund-entry-form"
                onSubmit={(e) => { e.preventDefault(); addMut.mutate(); }}>
            <div><Label className="overline">Entry</Label>
              <Select value={entry.type} onValueChange={(v) => setEntry((s) => ({ ...s, type: v }))}>
                <SelectTrigger data-testid="fund-entry-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ENTRY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="overline">Amount</Label><Input data-testid="fund-entry-amount" type="number" min="0.01" step="0.01" value={entry.amount} onChange={(e) => setEntry((s) => ({ ...s, amount: e.target.value }))} required /></div>
            <div><Label className="overline">Date</Label><DatePicker testId="fund-entry-date" value={entry.date} onChange={(v) => setEntry((s) => ({ ...s, date: v }))} /></div>
            <div><Label className="overline">Note</Label><Input data-testid="fund-entry-note" value={entry.note} onChange={(e) => setEntry((s) => ({ ...s, note: e.target.value }))} /></div>
            <Button type="submit" data-testid="fund-entry-save" disabled={addMut.isPending || !entry.amount}>Record</Button>
          </form>
        )}

        <div className="border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">{["Date", "Entry", "Note", "Amount", ""].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              <TableRow><TableCell className="mono text-xs">{fmtDate(fund.date)}</TableCell><TableCell className="text-sm">Initial allocation</TableCell><TableCell className="text-xs text-muted-foreground">—</TableCell>
                <TableCell className="mono text-sm">{fmtMoney(fund.initial_allocation)}</TableCell><TableCell /></TableRow>
              {fund.entries.map((e) => { const m = ENTRY_META[e.type]; const Icon = m.icon; return (
                <TableRow key={e.id} data-testid={`fund-entry-${e.id}`}>
                  <TableCell className="mono text-xs">{fmtDate(e.date)}</TableCell>
                  <TableCell className={`text-sm flex items-center gap-1.5 ${m.tone}`}><Icon className="size-3.5" /> {m.label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.note || "—"}
                    {e.source === "expense" && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200" data-testid={`fund-entry-from-expense-${e.id}`}>from expense</span>}
                    {e.has_receipt && <button type="button" className="ml-1.5 inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`fund-entry-receipt-${e.id}`} onClick={() => api.openFundEntryReceipt(fund.id, e.id).catch(onErr)}><Paperclip className="size-3" /> receipt</button>}</TableCell>
                  <TableCell className={`mono text-sm ${m.tone}`}>{e.type === "return" ? "+" : "−"}{fmtMoney(e.amount)}</TableCell>
                  <TableCell className="text-right">{fund.status === "open" && e.source !== "expense" && (
                    <Button variant="ghost" size="icon" className="size-7 text-red-600" data-testid={`fund-entry-delete-${e.id}`} onClick={() => delEntry.mutate(e.id)}><Trash2 className="size-3.5" /></Button>)}</TableCell>
                </TableRow>); })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" className="text-red-600 mr-auto" data-testid="fund-delete" disabled={delFund.isPending} onClick={() => delFund.mutate()}><Trash2 className="size-3.5 mr-1" /> Delete</Button>
          {fund.status === "open"
            ? <Button data-testid="fund-close" className="bg-slate-900 hover:bg-slate-800" disabled={statusMut.isPending} onClick={() => statusMut.mutate("closed")}><Lock className="size-3.5 mr-1" /> Close project &amp; lock profit</Button>
            : <Button variant="outline" data-testid="fund-reopen" disabled={statusMut.isPending} onClick={() => statusMut.mutate("open")}><Unlock className="size-3.5 mr-1" /> Reopen</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
